// src/utils/Nom035ReportGenerator.js

import jsPDF from "jspdf";
import Chart from "chart.js/auto";
import ChartDataLabels from "chartjs-plugin-datalabels";
import questionnaireScopeApi from "../api/questionnaireScopeApi";
import nom35AnalyticsApi from "../api/nom35AnalyticsApi";
import nom035Ranges from "../data/nom035Ranges";
import calculateNom035MinimumRespondents from "./nom035SampleSize";

// Registrar plugin y config global de data labels
Chart.register(ChartDataLabels);
Chart.defaults.set("plugins.datalabels", {
  anchor: "center",
  align: "center",
  color: "black",
  font: { size: 12 },
  formatter: (value) => value
});

const levelOrder = ["Nulo", "Bajo", "Medio", "Alto", "Muy alto"];
const defaultColors = ["#1069f1", "#00be9c", "#ffe951", "#FFA500", "red"];
const categoryKeys = Object.keys(nom035Ranges.categoryDomains);
const categoryLabels = {
  environment: "Ambiente de trabajo",
  factors: "Factores de la actividad",
  timeOrganization: "Organización del tiempo",
  leadershipRelations: "Liderazgo y relaciones",
  organizationalEnvironment: "Entorno organizacional"
};
const domainLabels = {
  conditions: "Condiciones ambientales",
  workload: "Carga de trabajo",
  control: "Falta de control",
  workday: "Jornada de trabajo",
  interference: "Interferencia en el trabajo",
  leadership: "Liderazgo",
  relationships: "Relaciones en el trabajo",
  violence: "Violencia",
  recognition: "Reconocimiento",
  belonging: "Sentido de pertenencia"
};
const currentYear = new Date().getFullYear();

/**
 * Carga una imagen vía URL y devuelve un objeto con DataURL y sus dimensiones originales
 */
function loadLogo(url) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => {
      const canvas = document.createElement("canvas");
      canvas.width = img.naturalWidth;
      canvas.height = img.naturalHeight;
      const ctx = canvas.getContext("2d");
      ctx.drawImage(img, 0, 0);
      resolve({
        dataURL: canvas.toDataURL("image/png"),
        width: img.naturalWidth,
        height: img.naturalHeight
      });
    };
    img.onerror = reject;
    img.src = url;
  });
}

/**
 * Renderiza un Chart.js en un canvas fijo y lo retorna
 */
async function renderChart({ type, data, options = {} }) {
  const canvas = document.createElement("canvas");
  canvas.width = 600;
  canvas.height = 300;
  const ctx = canvas.getContext("2d");
  new Chart(ctx, {
    type,
    data,
    plugins: [ChartDataLabels],
    options: {
      responsive: false,
      animation: false,
      maintainAspectRatio: false,
      plugins: { legend: { display: false } },
      ...options
    }
  });
  await new Promise((r) => requestAnimationFrame(r));
  return canvas;
}

export async function generateNom035Report(analyticsScopeId, filters = {}, metadataScopeId = analyticsScopeId) {
  const groupedApplicationIds = String(filters.applicationIds || "")
    .split(",")
    .map(id => id.trim())
    .filter(Boolean);
  const hasApplicationFilter = Boolean(filters.applicationId || groupedApplicationIds.length);
  const isGroupedReport = groupedApplicationIds.length > 1;
  const pdf = new jsPDF({ unit: "pt", format: "a4" });
  const { width: pageW, height: pageH } = pdf.internal.pageSize;
  const margin = 40;
  const headerH = 60;
  const contentW = pageW - margin * 2;
  const lineH = 14;

  // Cargar empresa/sucursal para portada. El scope de datos puede ser applicationId.
  const company = await questionnaireScopeApi.getById(metadataScopeId);
  const logoUrl = company.logoUrl || "/logo512.png";

  // Los totales corresponden al alcance solicitado (aplicación, sucursal o compañía).
  const [{ totalResponses }, { totalEmployees }] = await Promise.all([
    nom35AnalyticsApi.getResponsesCountByCompanyId(analyticsScopeId, filters),
    nom35AnalyticsApi.getRespondantsByCompanyId(
      metadataScopeId,
      filters
    ),
  ]);

  // Cálculo mínimo de encuestados
  const N = totalEmployees || 0;
  const minRespondents = calculateNom035MinimumRespondents(N);

  // Cargar logo (cover + header)
  let logo;
  try {
    logo = await loadLogo(logoUrl);
  } catch (e) {
    console.error("Error cargando logo:", e);
  }

  // Dibuja el header fijo en cada página (no en cover)
  function drawHeader() {
    pdf.setFillColor("#282c34");
    pdf.rect(0, 0, pageW, headerH, "F");
    if (logo) {
      const maxH = headerH - 10;
      const ratio = logo.width / logo.height;
      const drawH = maxH;
      const drawW = ratio * drawH;
      const x = (pageW - drawW) / 2;
      const y = (headerH - drawH) / 2;
      pdf.addImage(logo.dataURL, "PNG", x, y, drawW, drawH);
    }
  }

  function addCover() {
    let y = 80;
    pdf.setFont("helvetica", "bold").setFontSize(32);
    pdf.text("Deli Life Wellness", pageW / 2, y, { align: "center" });
    y += 30;
    if (logo) {
      const scale = Math.min(260 / logo.width, 150 / logo.height);
      const width = logo.width * scale;
      const height = logo.height * scale;
      pdf.addImage(logo.dataURL, "PNG", (pageW - width) / 2, y, width, height);
      y += height + 30;
    }
    const addText = (text, size = 12, bold = false) => {
      pdf.setFont("helvetica", bold ? "bold" : "normal").setFontSize(size);
      const lines = pdf.splitTextToSize(text, contentW);
      pdf.text(lines, margin, y);
      y += lines.length * (size + 3) + 8;
    };
    addText("Aplicación Cuestionario NOM-035", 22, true);
    addText("Identificación de los Trabajadores que Fueron Sujetos a Acontecimientos Traumáticos Severos\nIdentificación y Análisis de los Factores de Riesgo Psicosocial y Evaluación del Entorno Organizacional en los Centros de Trabajo");
    addText(`Empresa: ${company.name}`, 14, true);
    addText(`${hasApplicationFilter ? "Total de empleados" : "Registros en aplicaciones"}: ${N}`);
    addText(`Respuestas con los filtros seleccionados: ${totalResponses}`);
    if (filters.applicationId) {
      addText(`Mínimo de encuestados requeridos: ${minRespondents}`);
    } else if (isGroupedReport) {
      addText(`Resultados agrupados de ${groupedApplicationIds.length} aplicaciones; puede incluir personas repetidas.`);
    } else {
      addText("Consolidado de todas las aplicaciones; puede incluir personas repetidas.");
    }
    for (const [key, label] of [["ageRange", "Edad"], ["jobArea", "Área"], ["biologicalSex", "Sexo biológico"]]) {
      addText(`${label}: ${filters[key] || "Todos"}`);
    }
    addText(`Año de generación: ${currentYear}`);
  }

  // Wrap titles and legends before placing the chart and question details.
  async function addChartPage(title, canvas, isFirst, ranges) {
    if (!isFirst) pdf.addPage();
    drawHeader();
    let y = headerH + 30;
    pdf.setFont("helvetica", "bold").setFontSize(18);
    const titleLines = pdf.splitTextToSize(title, contentW);
    pdf.text(titleLines, margin, y);
    y += titleLines.length * 21 + 10;
    if (ranges) {
      pdf.setFont("helvetica", "normal").setFontSize(11);
      let x = margin;
      ranges.forEach((range, index) => {
        const text = `• ${levelOrder[index]} (${range})`;
        const width = pdf.getTextWidth(text) + 15;
        if (x + width > pageW - margin) { x = margin; y += lineH; }
        pdf.setTextColor(defaultColors[index]);
        pdf.text(text, x, y);
        x += width;
      });
      pdf.setTextColor("black");
      y += lineH + 10;
    }
    const imgH = canvas.height * contentW / canvas.width;
    pdf.addImage(canvas.toDataURL("image/png"), "PNG", margin, y, contentW, imgH);
    return y + imgH;
  }

  // Añade sección de detalles
  function addDetails(details, startY) {
    let y = startY + 20;
    pdf.setFont("helvetica", "normal").setFontSize(12);

    if (details.counts?.traumaticEvent != null) {
      pdf.text(`Eventos traumáticos: ${details.counts.traumaticEvent}`, margin, y);
      y += lineH;
      pdf.text(`Requiere atención médica: ${details.counts.requireClinicalAttention}`, margin, y);
      y += lineH;
      pdf.text(`No requiere atención médica: ${details.counts.noRequireClinicalAttention}`, margin, y);
      y += lineH;
    }
    if (details.averageScore != null) {
      pdf.text(
        `Puntaje promedio: ${details.averageScore.toFixed(2)}`,
        margin,
        y
      );
      y += lineH;
    }
    if (details.riskLevel) {
      pdf.text(`Riesgo del puntaje promedio: ${details.riskLevel}`, margin, y);
      y += lineH;
    }

    if (details.respondentCount === 0) {
      pdf.text("Sin respuestas para los filtros seleccionados.", margin, y);
      y += lineH;
    } else if (details.respondentCount > 0) {
      pdf.text(`Promedios sobre ${details.respondentCount} ${details.respondentCount === 1 ? "cuestionario" : "cuestionarios"}; no aplicables aportan 0.`, margin, y);
      y += lineH;
    }
    for (const q of details.questions || []) {
      const value = q.average == null ? "Sin respuestas aplicables" : q.average.toFixed(2);
      const count = q.responseCount == null ? "" : ` (${q.responseCount} ${q.responseCount === 1 ? "respuesta" : "respuestas"})`;
      const txt = `${q.question}: ${value}${count}`;
      const lines = pdf.splitTextToSize(txt, contentW);
      for (const line of lines) {
        if (y + lineH > pageH - margin) {
          pdf.addPage();
          drawHeader();
          y = headerH + 20;
        }
        pdf.text(line, margin, y);
        y += lineH;
      }
    }
  }

  // === Generación del PDF ===

  // 1) Portada
  addCover();

  // 2) Añadir página en blanco para la primera sección
  pdf.addPage();

  const sections = [];

  // Sección clínica como primer chart
  sections.push({
    title: "Cuestionario 1 - Acontecimientos Traumáticos Severos",
    genChart: async () => {
      const res = await nom35AnalyticsApi.getClinicalSummaryByCompanyId(
        analyticsScopeId,
        filters
      );
      const t = res.counts.traumaticEvent || 0;
      const r = res.counts.requireClinicalAttention || 0;
      const n = res.counts.noRequireClinicalAttention || 0;
      const max = Math.max(t, r, n, 1);
      const colors = ["#FFA500","red","#ffe951"]; // naranja, rojo, amarillo
      return renderChart({
        type: "bar",
        data: {
          labels: [
            "Eventos traumáticos",
            "Requiere atención médica",
            "No requiere atención médica"
          ],
          datasets: [{ data: [t, r, n], backgroundColor: colors }]
        },
        options: {
          scales: {
            y: { beginAtZero: true, max: max + 1, ticks: { stepSize: 1 } }
          }
        }
      });
    },
    genDetails: async () =>
      await nom35AnalyticsApi.getClinicalSummaryByCompanyId(analyticsScopeId, filters)
  });

  // Sección global
  sections.push({
    title: "Resumen General – Riesgo global",
    ranges: nom035Ranges.global.range,
    genChart: async () => {
      const res = await nom35AnalyticsApi.getSummaryByCompanyId(
        analyticsScopeId,
        "global",
        filters
      );
      const counts = levelOrder.map((l) => res.counts[l] || 0);
      const max = Math.max(...counts, 1);
      return renderChart({
        type: "bar",
        data: {
          labels: levelOrder,
          datasets: [{ data: counts, backgroundColor: defaultColors }]
        },
        options: {
          scales: {
            y: { beginAtZero: true, max: max + 1, ticks: { stepSize: 1 } }
          }
        }
      });
    },
    genDetails: async () =>
      analyticsScopeId
        ? await nom35AnalyticsApi.getQuestionsByCompanyId(
            analyticsScopeId,
            "global",
            filters
          )
        : await nom35AnalyticsApi.getQuestionsGlobal("global", filters)
  });

  // Puntaje por área de trabajo (Global)
  sections.push({
    title: "Puntaje por Área de Trabajo (Global)",
    ranges: nom035Ranges.global.range,
    genChart: async () => {
      const { jobareas } =
        await nom35AnalyticsApi.getJobAreasSummaryByCompanyId(
          metadataScopeId,
          "global",
          filters
        );
      const scores = jobareas.map((a) => a.score);
      const max = Math.ceil(Math.max(...scores, 1));
      const colors = jobareas.map(
        (a) => defaultColors[levelOrder.indexOf(a.risk)]
      );
      return renderChart({
        type: "bar",
        data: {
          labels: jobareas.map((a) => a.jobArea),
          datasets: [{ data: scores, backgroundColor: colors }]
        },
        options: {
          scales: {
            y: { beginAtZero: true, max: max + 1, ticks: { stepSize: 1 } }
          }
        }
      });
    }
  });

  // Categorías y dominios
  for (let cat of categoryKeys) {
    sections.push({
      title: `Resumen Categoría – ${categoryLabels[cat]}`,
      ranges: nom035Ranges.categoryDomains[cat].range,
      genChart: async () => {
        const res = await nom35AnalyticsApi.getSummaryByCompanyId(
          analyticsScopeId,
          cat,
          filters
        );
        const counts = levelOrder.map((l) => res.counts[l] || 0);
        const max = Math.max(...counts, 1);
        return renderChart({
          type: "bar",
          data: {
            labels: levelOrder,
            datasets: [{ data: counts, backgroundColor: defaultColors }]
          },
          options: {
            scales: {
              y: { beginAtZero: true, max: max + 1, ticks: { stepSize: 1 } }
            }
          }
        });
      },
      genDetails: async () =>
        analyticsScopeId
          ? await nom35AnalyticsApi.getQuestionsByCompanyId(
              analyticsScopeId,
              cat,
              filters
            )
          : await nom35AnalyticsApi.getQuestionsGlobal(cat, filters)
    });

    const doms = Object.keys(nom035Ranges.categoryDomains[cat].domains);
    for (let dom of doms) {
      sections.push({
        title: `Dominio – ${domainLabels[dom]}`,
        ranges: nom035Ranges.categoryDomains[cat].domains[dom].range,
        genChart: async () => {
          const res = await nom35AnalyticsApi.getSummaryByCompanyId(
            analyticsScopeId,
            dom,
            filters
          );
          const counts = levelOrder.map((l) => res.counts[l] || 0);
          const max = Math.max(...counts, 1);
          return renderChart({
            type: "bar",
            data: {
              labels: levelOrder,
              datasets: [{ data: counts, backgroundColor: defaultColors }]
            },
            options: {
              scales: {
                y: { beginAtZero: true, max: max + 1, ticks: { stepSize: 1 } }
              }
            }
          });
        },
        genDetails: async () =>
          analyticsScopeId
            ? await nom35AnalyticsApi.getQuestionsByCompanyId(
                analyticsScopeId,
                dom,
                filters
              )
            : await nom35AnalyticsApi.getQuestionsGlobal(dom, filters)
      });
    }

    sections.push({
      title: `Puntaje por Área de Trabajo – ${categoryLabels[cat]}`,
      ranges: nom035Ranges.categoryDomains[cat].range,
      genChart: async () => {
        const { jobareas } =
          await nom35AnalyticsApi.getJobAreasSummaryByCompanyId(
            metadataScopeId,
            cat,
            filters
          );
        const scores = jobareas.map((a) => a.score);
        const max = Math.ceil(Math.max(...scores, 1));
        const colors = jobareas.map(
          (a) => defaultColors[levelOrder.indexOf(a.risk)]
        );
        return renderChart({
          type: "bar",
          data: {
            labels: jobareas.map((a) => a.jobArea),
            datasets: [{ data: scores, backgroundColor: colors }]
          },
          options: {
            scales: {
              y: { beginAtZero: true, max: max + 1, ticks: { stepSize: 1 } }
            }
          }
        });
      }
    });
  }

  // Generar cada sección
  for (let i = 0; i < sections.length; i++) {
    const sec = sections[i];
    const canvas = await sec.genChart();
    const bottomY = await addChartPage(
      sec.title,
      canvas,
      i === 0,
      sec.ranges
    );
    if (sec.genDetails) {
      const det = await sec.genDetails();
      addDetails(det, bottomY);
    }
  }

  // Guardar PDF
  pdf.save(`Reporte_NOM035_${company.name}.pdf`);
}
